import type { EChartsOption } from 'echarts';
import { soccerZToChartZ } from 'pages/MeasurementDetail/coords';
import type { CornerZoneDto, CornerSide, MeasurementMarkerDto } from 'types/models/SessionsPageData';
import type { ZoneStat } from './playerPerformanceUtils';
import {
  buildPitchBaseSeries,
  zoneFillColor,
  toChartPolygon,
  polygonCentroid,
} from './freeKickZonePitchOptions';
import {
  GOAL_POST,
  GOAL_DEPTH,
  P_BOX_Z,
  P_BOX_X,
  SIX_BOX_Z,
  SIX_BOX_X,
  PENALTY_SPOT_X,
  ARC_RADIUS,
  PITCH_GREEN,
  LINE_COLOR,
  NET_COLOR,
  LINE_WIDTH_MAJOR,
  LINE_WIDTH_FRAME,
  NET_LINE_WIDTH,
  buildTopViewPitchOption,
  CAMERA_GOAL_AREA_FOCUS,
  TopViewPitchConfig,
} from 'pages/Sessions/charts/topViewPitchOptions';

/** Data item carried on a zone `custom` series entry. */
interface ZoneSeriesDataItem {
  value: number;
  zoneId: number;
  kickCount: number;
  sessionCount: number;
  onTargetPct: number | null;
}

interface ZoneItemStyle {
  color?: string;
  opacity?: number;
  borderColor?: string;
  borderWidth?: number;
  zoneDotted?: boolean;
  zoneCornerRadius?: number;
}

interface KickPointDataItem {
  value: [number, number];
  itemStyle: {
    color: string;
    opacity: number;
    borderColor: string;
    borderWidth: number;
  };
}


/**
 * Shrink a polygon inward by moving each point slightly toward the centroid.
 * Margin is applied in the direction from centroid to point.
 * @param polygon - Array of [z, x] points
 * @param marginPx - Margin in pixels (scaled by distance factor)
 */
function shrinkPolygonByMargin(polygon: [number, number][], marginPx: number): [number, number][] {
  if (polygon.length < 3) return polygon;
  const centroid = polygonCentroid(polygon);
  const scaleFactor = marginPx / 100; // Convert pixels to coordinate units
  
  return polygon.map(([z, x]) => {
    const dx = z - centroid[0];
    const dz = x - centroid[1];
    const distance = Math.sqrt(dx * dx + dz * dz);
    
    if (distance < 0.01) return [z, x]; // Point is at centroid
    
    // Move point toward centroid by scaleFactor
    return [
      z - (dx / distance) * scaleFactor,
      x - (dz / distance) * scaleFactor,
    ] as [number, number];
  });
}
// Camera bounds: same as TOP VIEW for consistency
// Shows goal area + penalty box with buffers
const VIEW_Z = 36.3;        // Lateral half-width with padding
const VIEW_X_MIN = -8.064;  // 40% buffer below goal line
const VIEW_X_MAX = 28.224;  // Penalty box + 40% buffer upfield
const CAMERA_X_MIN = VIEW_X_MIN;
const CAMERA_X_MAX = VIEW_X_MAX;
const KEEPER_BOX_LINE_COLOR = 'rgba(255,255,255,0.52)';
const KEEPER_BOX_SERIES_IDS = new Set([
  'penalty-box-top',
  'penalty-box-left',
  'penalty-box-right',
  'six-yard-box-top',
  'six-yard-box-left',
  'six-yard-box-right',
]);
const PENALTY_SPOT_SERIES_ID = 'penalty-spot';

// NOTE: Corner flags are now rendered via the unified buildTopViewPitchOption()
// when showCornerFlags: true is set. This eliminates duplication.

interface CornerZoneOptionArgs {
  zones: CornerZoneDto[];
  stats: Map<number, ZoneStat>;
  kicks: MeasurementMarkerDto[];
  selected: Set<number>;
  side?: CornerSide;
}

export function buildCornerZoneOption(args: CornerZoneOptionArgs): EChartsOption {
  const { zones, stats, kicks, selected, side = 'Left' } = args;

  // Use unified builder with corner-specific camera config + corner flags
  const pitchBaseOption = buildTopViewPitchOption({
    camera: CAMERA_GOAL_AREA_FOCUS,  // Same bounds as TOP VIEW
    showStripes: true,
    showPenaltyBox: true,
    showSixYardBox: true,
    showCenterCircle: false,  // Hide center (outside corner zone view)
    showGoalNet: true,
    showGoal: true,
    showPenaltySpot: true,
    showCornerFlags: true,  // Include corner flags from unified builder
    showPenaltyArc: true,
    showTouchlines: true,
    grassColor: PITCH_GREEN,
    lineColor: LINE_COLOR,
    zIndex: 0,
  });

  // Merge base series from unified builder with corner-specific elements
  const pitchBaseSeries = ((pitchBaseOption.series as any[]) || []).map((series) => {
    if (KEEPER_BOX_SERIES_IDS.has(series?.id)) {
      return {
        ...series,
        lineStyle: {
          ...(series.lineStyle ?? {}),
          color: KEEPER_BOX_LINE_COLOR,
        },
      };
    }

    if (series?.id === PENALTY_SPOT_SERIES_ID) {
      return {
        ...series,
        itemStyle: {
          ...(series.itemStyle ?? {}),
          color: KEEPER_BOX_LINE_COLOR,
        },
      };
    }

    return series;
  });
  const selectionActive = selected.size > 0;

  const zoneSeries = zones.map((z) => {
    const isSel = selected.has(z.zoneId);
    const stat = stats.get(z.zoneId);
    const dataItem: ZoneSeriesDataItem = {
      value: 0,
      zoneId: z.zoneId,
      kickCount: stat?.kickCount ?? 0,
      sessionCount: stat?.sessionCount ?? 0,
      onTargetPct: stat?.onTargetPct ?? null,
    };
    // Zones are already split by side (Left z > 0, Right z < 0), no flipping needed
    const chartPolygon = toChartPolygon(z.outline);
    const shrunkPolygon = shrinkPolygonByMargin(chartPolygon, 5); // 5px margins
    return {
      id: `corner-zone-${z.zoneId}`,
      type: 'custom' as const,
      z: 1,
      silent: false,
      animation: false,
      data: [dataItem],
      polygon: shrunkPolygon,
      itemStyle: {
        color: zoneFillColor(stats.get(z.zoneId)),
        opacity: !selectionActive ? 0.5 : (isSel ? 0.85 : 0.15), // Less opacity when not selected
        borderColor: !selectionActive ? 'rgba(255,255,255,0.6)' : (isSel ? '#ffffff' : 'rgba(255,255,255,0.1)'),
        borderWidth: !selectionActive ? 1.5 : (isSel ? 3.5 : 0.8), // More distinctive lines
      },
      zoneDotted: true, // Always use dotted lines
      zoneCornerRadius: 8, // Increased corner radius
    };
  });

  const selectedZoneId = selectionActive ? Array.from(selected)[0] : undefined;

  const kickPoints: KickPointDataItem[] = kicks
    .filter((m) => {
      if (m.kickX == null || m.kickZ == null || !m.cornerZones) return false;
      return true;
    })
    .map((m) => {
      // For corner zones, we need to key by the selected layout
      // This is passed separately, so for now we color by first available zone
      const zoneId = m.cornerZones ? Object.values(m.cornerZones)[0] : undefined;
      return {
        value: [soccerZToChartZ(m.kickZ as number), m.kickX as number],
        itemStyle: {
          color: zoneFillColor(zoneId != null ? stats.get(zoneId) : undefined),
          opacity: !selectionActive || zoneId === selectedZoneId ? 0.9 : 0.2,
          borderColor: 'rgba(0,0,0,0.45)',
          borderWidth: 1,
        },
      };
    });

  // Don't draw kick points for corner zones - zones themselves show aggregated data
  const kickSeries = {
    id: 'corner-kicks',
    type: 'scatter' as const,
    z: 3,
    symbolSize: 0,
    animation: false,
    silent: false,
    data: [],
  };


  // Use fixed camera view for both left and right — exactly matches TOP VIEW
  // No panning based on side selection; consistent view maintains correct proportions
  const xAxisMin = -36.3;  // Same as TOP VIEW
  const xAxisMax = 36.3;   // Same as TOP VIEW

  return {
    backgroundColor: PITCH_GREEN,
    tooltip: { show: false },
    grid: pitchBaseOption.grid as any,
    xAxis: {
      type: 'value' as const,
      min: xAxisMin,
      max: xAxisMax,
      splitLine: { show: false },
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { show: false },
    } as any,
    yAxis: {
      type: 'value' as const,
      min: VIEW_X_MIN,
      max: VIEW_X_MAX,
      inverse: true,
      splitLine: { show: false },
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { show: false },
    } as any,
    animationDuration: 0,
    series: [...pitchBaseSeries, ...zoneSeries, kickSeries] as EChartsOption['series'],
  };
}
